import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setConversationMessages,
  clearConversationMessages,
} from "store/AiFeatures/GoalsCreateWithNovaAI/action";
import useNovaAiResponseApi from "./useNovaAiResponseApi";
import useApplicationTracker from "customHooks/useApplicationTracker";
import { selectChatMessages } from "utils/getNovaConversationDetails";
const useNovaAiModalBody = ({
  newConversationId,
  chatPage,
  setChatPage,
  scrollToBottom,
  setScrollToBottom,
}) => {
  const { propsForGoalsNovaAi } = useNovaAiResponseApi();
  const {
    regenerateChatMessage,
    fetchConversationMessages,
    sendNovaChatFeedback,
    isAiLoading,
  } = propsForGoalsNovaAi;

  const dispatch = useDispatch();
  const prevScrollPosition = useRef(0);
  const { captureUserEvent } = useApplicationTracker();
  const { accountId, employeeId } = useSelector((state) => state.user);
  const data = JSON.parse(
    localStorage.getItem("nova_conversation_data") || "{}"
  );
  const conversationId = data[employeeId]?.id;
  const userNovaChatMessages = useSelector((state) =>
    selectChatMessages(state, conversationId)
  );
  const { hasMoreMessages } = useSelector(
    (state) => state.aiFeatures.goalsCreateWithNovaAI
  );
  const userMessageId = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.userMessageId
  );
  const scrollDirection = useSelector(
    (state) => state.aiFeatures.goalsCreateWithNovaAI.scrollDirection
  );

  const [scrollContainer, setScrollContainer] = useState(null);
  const [lastScrollDirection, setLastScrollDirection] = useState("append");
  const [isScrolling, setIsScrolling] = useState(false);
  const [fromRegenerate, setFromRegenerate] = useState(false);

  const latestAssistantMessage = [...userNovaChatMessages]
    .reverse()
    .find((msg) => msg.role === "assistant");

  useEffect(() => {
    if (conversationId) {
      dispatch(setConversationMessages(conversationId));
      setScrollToBottom(true);
    }

    if (newConversationId) {
      dispatch(clearConversationMessages(newConversationId));
    }
  }, [conversationId, newConversationId, dispatch]);

  useEffect(() => {
    if (!scrollContainer || userNovaChatMessages?.length === 0) return;

    const { scrollTop, clientHeight, scrollHeight } = scrollContainer;

    const isUserNearBottom = scrollHeight - (scrollTop + clientHeight) <= 150;

    if (scrollToBottom && scrollDirection !== "prepend") {
      setTimeout(() => {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth",
        });
        setScrollToBottom(false);
      }, 0);
      return;
    }

    if (scrollDirection === "append" && isUserNearBottom) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [
    userNovaChatMessages,
    scrollContainer,
    scrollDirection,
    scrollToBottom,
    setScrollToBottom,
  ]);

  useEffect(() => {
    const length = userNovaChatMessages?.length || 0;
    const lastMessage = length > 0 ? userNovaChatMessages[length - 1] : null;

    if (
      length > 0 &&
      (regenerateChatMessage || lastMessage?.role === "assistant")
    ) {
      setScrollToBottom(true);
    }
  }, [userNovaChatMessages]);

  const handleScroll = async (event) => {
    if (isScrolling || !hasMoreMessages) return;

    const container = event.target;
    const { scrollTop, clientHeight } = container;
    const currentScrollBottom = scrollTop + clientHeight;
    if (!scrollTop && scrollTop === 0 && hasMoreMessages) {
      setIsScrolling(true);

      const previousScrollHeight = container.scrollHeight;

      await fetchConversationMessages({
        page: chatPage + 1,
        append: true,
        direction: "prepend",
      });
      setChatPage((prev) => prev + 1);
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        const scrollDiff = newScrollHeight - previousScrollHeight;
        container.scrollTop = scrollDiff;
        setIsScrolling(false);
      });

      setLastScrollDirection("prepend");
      return;
    }
    if (!hasMoreMessages && currentScrollBottom > prevScrollPosition.current) {
      setLastScrollDirection("append");
      setScrollToBottom(false);
    }
    prevScrollPosition.current = currentScrollBottom;
  };

  const regenerateCurrentResponse = async ({ userMessageId }) => {
    await regenerateChatMessage({
      conversationId: newConversationId,
      userMessageId,
      setFromRegenerate,
    });
    captureUserEvent("nova_regenerate_response_button_clicked", {
      accountId: accountId,
      messageId: userMessageId,
    });
  };

  return {
    scrollContainer,
    setScrollContainer,
    lastScrollDirection,
    isScrolling,

    latestAssistantMessage,
    userNovaChatMessages,
    hasMoreMessages,
    userMessageId,
    conversationId,

    handleScroll,
    regenerateCurrentResponse,
    sendNovaChatFeedback,
    isAiLoading,
    fromRegenerate,
    scrollToBottom,
    setScrollToBottom,
  };
};

export default useNovaAiModalBody;
